import { ComponentProps } from 'react';
import { useMutation } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useBoolean } from 'usehooks-ts';
import { Box, HStack, useTheme, VStack } from 'native-base';
import { startCase, toLower } from 'lodash';

import { Button, ControlInput, Text } from '@pimm/base';
import { ChangePin, ValidatePin } from '@pimm/services/lib/sms-tenants/services';
import { ChangePinRequest, PinResetDto, UserBySiteKeyDto } from '@pimm/services/lib/sms-tenants';
import { PinCode } from '@app/components/shared';
import { useLiveChat } from '@app/features/live-chat/store/chat.store';
import { useFormPinSetup } from '../hooks/form-pin-setup.hook';

type FormUserPasskeyProps = Pick<ComponentProps<typeof PinCode>, 'headerComponent' | 'footerComponent' | 'submitLabel'> & {
  siteId?: string;
  user?: Partial<UserBySiteKeyDto>;
  onChange?: (_user: Partial<UserBySiteKeyDto>, _result?: PinResetDto) => void;
  onClose: () => void;
  onSuccess: (isSuccess: boolean) => void;
};

export const FormUserPasskey = ({ siteId, user, ...props }: FormUserPasskeyProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const isPinSetup = useBoolean();
  const form = useFormPinSetup();

  const changePin = useMutation({
    mutationFn: ChangePin,
    onSuccess: _result => {
      if (user) props.onChange?.(user, _result);
      isPinSetup.setFalse();
    },
    onError: (error: any) => {
      // Reset input fields but keep the error message
      form.reset(undefined, { keepErrors: true });
      form.setError('root', {
        type: 'custom',
        message: (error.code ?? error.status) === 400 ? 'store_core:invalid_password' : 'store_core:error_try_again',
      });
    },
  });

  const validatePin = useMutation({
    mutationFn: ValidatePin,
    onSuccess: () => {
      useLiveChat.getState().reset();
      props.onSuccess(true);
    },
  });

  const errMessage = form.formState.errors.root?.message;
  const name = startCase(toLower([user?.firstName, user?.lastName].filter(Boolean).join(' ')));

  const handleChangePin = () => {
    const { currentPassword, pin, confirmPin } = form.getValues();

    if (!user?.id) return;
    if (pin !== confirmPin) {
      form.setError('confirmPin', { type: 'custom', message: 'store_core:pin_mismatch' });
      return;
    }
    const payload: ChangePinRequest = {
      userId: user.id,
      currentPassword: btoa(currentPassword),
      newPin: pin,
    };
    changePin.mutate(payload);
  };

  const handleValidatePin = async (pincode: string): Promise<void> => {
    if (!user?.id || !siteId) return;
    await validatePin.mutateAsync({
      userId: user.id,
      siteId: siteId!,
      pin: pincode,
    });
  };

  // Setup / Reset Pin
  if (isPinSetup.value) {
    return (
      <VStack space={4} w="full">
        {/* Header */}
        <VStack>
          <Text size="2xl" fontWeight={700} color="gray.900">
            {t('store_core:reset_pin')}
          </Text>

          <Text color="black" fontWeight={500} size="md">
            {t('store_core:user')}:{' '}
            <Text color="black" fontWeight={700} size="md">
              {name}
            </Text>
          </Text>
          {!!user?.userName && (
            <Text color="black" fontWeight={500} size="md">
              {t('store_core:username')}:{' '}
              <Text color="black" fontWeight={700} size="md">
                {user?.userName}
              </Text>
            </Text>
          )}
        </VStack>

        {/* Form */}
        <VStack space={2} justifyContent="center">
          <VStack alignItems="flex-start" justifyContent="center">
            <Text size="md" fontWeight={500} color="gray.900">
              {t('store_core:current_password')}
            </Text>
            <HStack space={2} alignItems="center">
              <ControlInput
                control={form.control}
                type="password"
                name="currentPassword"
                _helperText={{ textAlign: 'left' }}
                _input={{ minW: 280 }}
                renderErrorText={_err => t(_err ?? '')}
              />
            </HStack>
          </VStack>

          <VStack alignItems="flex-start" justifyContent="center">
            <Text size="md" fontWeight={500} color="gray.900">
              {t('store_core:new_pin')}
            </Text>
            <HStack space={2} alignItems="center">
              <ControlInput
                numericOnly
                control={form.control}
                type="password"
                name="pin"
                _helperText={{ textAlign: 'center' }}
                _input={{ minW: 280, maxLength: 4, keyboardType: 'numeric' }}
                renderErrorText={_err => t(_err ?? '')}
              />
            </HStack>
          </VStack>

          <VStack alignItems="flex-start" justifyContent="center">
            <Text size="md" fontWeight={500} color="gray.900">
              {t('store_core:confirm_pin')}
            </Text>
            <HStack space={2} alignItems="center">
              <ControlInput
                numericOnly
                control={form.control}
                type="password"
                name="confirmPin"
                _helperText={{ textAlign: 'center' }}
                _input={{ minW: 280, maxLength: 4, keyboardType: 'numeric' }}
                renderErrorText={_err => t(_err ?? '')}
              />
            </HStack>

            <Box pt={1}>
              <Text color="gray.600" fontWeight={500} size="md" lineHeight="md" italic>
                {t('store_core:pin_must_be_4_digits')}
              </Text>
            </Box>
          </VStack>

          {!!errMessage && (
            <Text size="md" color="error.700" lineHeight="sm">
              {t(errMessage)}
            </Text>
          )}
        </VStack>

        <HStack space={3} justifyContent="flex-end" mt={4} width="full">
          <Button
            variant="unstyled"
            shadow={0}
            bg="white"
            flex={1}
            borderColor="gray.300"
            disabled={changePin.isLoading}
            onPress={props.onClose}
          >
            {t('store_core:cancel')}
          </Button>

          <Button flex={1} shadow={0} isLoading={changePin.isLoading} disabled={!form.formState.isValid} onPress={handleChangePin}>
            {t('store_core:save')}
          </Button>
        </HStack>
      </VStack>
    );
  }

  // Setup Prompt Message
  if (changePin.isIdle && user && !user.pinIsSet) {
    return (
      <VStack space={4}>
        <VStack space={2}>
          <Text size="2xl" fontWeight={700} color="gray.900">
            {t('store_core:pin_not_set_up')}
          </Text>
          <Text size="md" color={colors.gray[800]} fontWeight={500} lineHeight="md">
            <Text size="md" fontWeight={600} color="black">
              {name}
            </Text>{' '}
            {t('store_core:setup_pin_prompt')}
          </Text>
        </VStack>

        <HStack space={2}>
          <Button variant="unstyled" flex={1} shadow={0} borderColor="gray.300" bg="white" onPress={props.onClose}>
            {t('store_core:no')}
          </Button>

          <Button flex={1} shadow={0} onPress={() => isPinSetup.setTrue()}>
            {t('store_core:yes')}
          </Button>
        </HStack>
      </VStack>
    );
  }

  // Pin Validation
  return (
    <PinCode
      headerComponent={
        props.headerComponent ?? (
          <VStack pb={3} w="full">
            <Text size="xl" color="gray.900" fontWeight={700}>
              {t('store_core:pin_required')}
            </Text>
            <Text size="md" color="black" fontWeight={500}>
              {t('store_core:enter_pin_prompt')}:
            </Text>
            {(!!user?.firstName || !!user?.lastName) && (
              <Text size="lg" color="gray.900" fontWeight={700}>
                {name}
              </Text>
            )}
          </VStack>
        )
      }
      footerComponent={
        props.footerComponent ?? (
          <Button variant="unstyled" borderWidth={0} _text={{ color: 'primary.600', fontWeight: 500 }} onPress={() => isPinSetup.setTrue()}>
            {t('store_core:reset_pin_code')}
          </Button>
        )
      }
      submitLabel={props.submitLabel}
      onCancel={props.onClose}
      onValidate={handleValidatePin}
    />
  );
};
